import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as QuillCore from 'quill';
import { Delta, Op } from 'quill';

import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

export interface RichEditorRef {
  getHtml: () => string;
  getDelta: () => Op[];
}

interface RichEditorProps {
  value?: { type: string, text: string };
  onChange?: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
}

const FormRichEditor = forwardRef<RichEditorRef, RichEditorProps>(
  ({ maxLength = 5000, placeholder = '', value, onChange }, ref) => {
    const quillRef = useRef<QuillCore.default | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div')
      );

      const quill = new QuillCore.default(editorContainer, {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [
              'bold',
              'italic',
              'underline',
              'strike',
              'link',
              { color: [] },
              { background: [] },
            ],
            [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }],
            ['clean'],
          ],
        },
        placeholder,
        theme: 'snow',
      });

      quillRef.current = quill;

      return () => {
        container.innerHTML = '';
        quillRef.current = null;
      };
    }, [])

    useEffect(() => {
      const quill = quillRef.current!;
      if (value) {
        const { text } = value;
        if (text) {
          try {
            quill.setContents(JSON.parse(text));
          } catch (error) {
            console.error('解析富文本内容失败:', error);
          }
        }
      }
    }, [value]);

    useEffect(() => {
      const quill = quillRef.current!;

      // 监听文本变化, 限制最大长度
      quill.on('text-change', () => {
        const delta = quill.getContents();
        const length = delta.reduce(
          (sum: number, op: Op) =>
            sum + (op.insert ? (op.insert as string).length : 0),
          0
        );
        if (length > maxLength) {
          quill.deleteText(maxLength, length - maxLength);
        }

        const [htmlContent, deltaContent] = [quill.getSemanticHTML?.() || '', quill.getContents?.() || ''];
        const data = JSON.stringify({
          type: 'text',
          htmlContent,
          deltaContent: JSON.stringify(deltaContent),
        });
        onChange?.(data)
      });

      return () => {
        quill.off('text-change');
      };
    }, [maxLength, onChange]);

    useImperativeHandle(ref, () => ({
      getHtml: () => quillRef.current?.getSemanticHTML() || '',
      getDelta: () => quillRef.current?.getContents().ops || [],
      setContents: (value: string) => quillRef.current?.setContents(value)
    }));

    return <div ref={containerRef}></div>;
  }
);

FormRichEditor.displayName = 'FormRichEditor';

export default FormRichEditor;
